import type {
  ICaaAuthorizeUrlParams,
  ICaaExchangeCodeParams,
  ICaaLogoutUrlParams,
  ICaaRefreshTokenParams,
  ICaaToken,
  ICaaTokenResponse,
} from "../interface/customer-account";
import { discoverCaaEndpoints } from "./caaDiscovery";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "./pkce";

/**
 * Customer Account API OAuth 2.0 Authorization Code + PKCE 흐름 (로그인 리다이렉트 → code 교환 → 토큰 갱신)
 * 토스트·라우팅·store는 스토어마다 달라 여기서 다루지 않고, 앱이 결과/콜백으로 처리
 * https://shopify.dev/docs/api/customer
 */

const CODE_VERIFIER_KEY = "caaCodeVerifier";
const STATE_KEY = "caaState";

const DEFAULT_SCOPE = "openid email customer-account-api:full";

/** token endpoint가 요청을 거부했을 때 (HTTP 4xx/5xx) — 네트워크 오류와 구분하기 위한 에러 */
export class CaaTokenRejectedError extends Error {}

/** token endpoint 호출 — authorization_code 교환과 refresh_token 갱신이 응답 형태가 같아 함께 사용 */
const requestCaaToken = async (shopDomain: string, params: Record<string, string>): Promise<ICaaToken> => {
  const { tokenEndpoint } = await discoverCaaEndpoints(shopDomain);

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(params),
  });

  if (!response.ok) throw new CaaTokenRejectedError(`[CAA] token request failed (${params.grant_type}): ${response.status}`);

  const data = (await response.json()) as ICaaTokenResponse;

  return {
    accessToken: data.access_token,
    // Shopify가 refresh 시 새 refresh_token을 내려줄 수 있어 매번 덮어씌움
    refreshToken: data.refresh_token,
    idToken: data.id_token,
    expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString(),
  };
};

/**
 * refresh_token으로 새 토큰을 발급받음
 * token endpoint가 거부하면 CaaTokenRejectedError, 네트워크 오류면 fetch 에러를 그대로 던짐
 */
export const refreshCaaToken = ({ shopDomain, clientId, refreshToken }: ICaaRefreshTokenParams): Promise<ICaaToken> =>
  requestCaaToken(shopDomain, { grant_type: "refresh_token", client_id: clientId, refresh_token: refreshToken });

/**
 * OAuth 인증 페이지 URL을 만듦 — code_verifier/state는 콜백(exchangeCaaCode)에서 검증하도록 sessionStorage에 저장
 */
export const buildCaaAuthorizeUrl = async ({
  shopDomain,
  clientId,
  redirectUri,
  scope = DEFAULT_SCOPE,
  loginHint,
}: ICaaAuthorizeUrlParams): Promise<string> => {
  const { authorizationEndpoint } = await discoverCaaEndpoints(shopDomain);

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  // 입력한 이메일을 Shopify 로그인 화면에 미리 채움
  if (loginHint) params.set("login_hint", loginHint);

  return `${authorizationEndpoint}?${params.toString()}`;
};

/**
 * 콜백으로 돌아온 authorization code를 토큰으로 교환
 * state가 맞지 않거나 교환에 실패하면 throw — code_verifier/state는 성공 여부와 무관하게 1회용이라 먼저 지움
 */
export const exchangeCaaCode = async ({ shopDomain, clientId, redirectUri, code, state }: ICaaExchangeCodeParams): Promise<ICaaToken> => {
  const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);

  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  if (!code || !state || !codeVerifier || state !== expectedState) {
    throw new Error("[CAA] invalid authorization callback");
  }

  return requestCaaToken(shopDomain, {
    grant_type: "authorization_code",
    client_id: clientId,
    redirect_uri: redirectUri,
    code,
    code_verifier: codeVerifier,
  });
};

/**
 * Shopify 로그인 세션을 종료하는 로그아웃 URL을 만듦
 * 앱 store만 비우면 Shopify 세션이 남아, 다음 로그인 시 계정 입력 없이 이전 계정으로 바로 로그인됨
 * postLogoutRedirectUri는 Shopify 관리자의 Customer Account API 설정에 로그아웃 URI로 등록돼 있어야 함
 */
export const buildCaaLogoutUrl = async ({ shopDomain, idToken, postLogoutRedirectUri }: ICaaLogoutUrlParams): Promise<string> => {
  const { endSessionEndpoint } = await discoverCaaEndpoints(shopDomain);

  const params = new URLSearchParams({ id_token_hint: idToken, post_logout_redirect_uri: postLogoutRedirectUri });

  return `${endSessionEndpoint}?${params.toString()}`;
};
